import { createContext, ReactNode, useContext, useRef } from 'react';

import { useJobPostingContext } from '@/contexts/job-posting-context';

import { ChannelFormState } from '../types';
import { useChannelForm } from './use-channel-form';

interface ChannelFormContextValue extends ReturnType<typeof useChannelForm> {
  channelSlug: string;
}

const ChannelFormContext = createContext<ChannelFormContextValue | null>(null);

export function ChannelFormProvider({
  channelSlug,
  children,
}: {
  channelSlug: string;
  children: ReactNode;
}) {
  const { hookForm } = useJobPostingContext();
  const { getValues } = hookForm;

  // Capture snapshot of form state when component mounts to revert to when canceling changes
  const snapshotBeforeEditRef = useRef<ChannelFormState>(
    JSON.parse(
      JSON.stringify(getValues(`channels.${channelSlug}`)),
    ) as ChannelFormState,
  );

  const channelFormValue = useChannelForm({
    channelSlug,
    snapshotBeforeEditRef,
  });

  return (
    <ChannelFormContext.Provider value={{ ...channelFormValue, channelSlug }}>
      {children}
    </ChannelFormContext.Provider>
  );
}

export function useChannelFormContext() {
  const context = useContext(ChannelFormContext);
  if (!context) {
    throw new Error(
      'useChannelFormContext must be used within ChannelFormProvider',
    );
  }
  return context;
}
